/* © NXAPP Limited */
import { useState, useMemo, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Papa from 'papaparse'
import { supabase } from '@/lib/supabase'
import { describeError } from '@/lib/describeError'
import {
  Upload, CheckCircle2, AlertCircle, X, Link2, CheckSquare, Square,
  Send, Plus, Trash2, RefreshCw, Users, UserPlus,
} from 'lucide-react'
import { authedFetch } from '@/lib/authedFetch'

interface ImportedLead {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  company: string | null
  batch: string | null
  domain_verified: boolean | null
  contact_id: string | null
  created_at: string
}
interface Campaign {
  id: string; name: string; description: string | null
  audience: 'director' | 'company'
  kind: 'broadcast' | 'sequence'
}

const NONE = '__none__'

function emailDomain(email: string): string | null {
  const at = email.lastIndexOf('@')
  if (at < 0) return null
  const d = email.slice(at + 1).trim().toLowerCase()
  return d || null
}

// Free MX check straight from the browser (Cloudflare DoH is CORS-enabled)
async function domainHasMx(domain: string): Promise<boolean> {
  try {
    const res = await fetch(`https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(domain)}&type=MX`,
      { headers: { Accept: 'application/dns-json' } })
    if (!res.ok) return false
    const data = await res.json() as { Status: number; Answer?: { type: number }[] }
    return data.Status === 0 && !!data.Answer?.some(a => a.type === 15)
  } catch { return false }
}

const guess = (headers: string[], ...names: string[]) =>
  headers.find(h => names.some(n => h.toLowerCase().replace(/[^a-z]/g, '').includes(n))) ?? NONE

export default function LeadsImportPage() {
  const qc = useQueryClient()
  const [userId, setUserId] = useState<string | null>(null)
  useEffect(() => { supabase.auth.getUser().then(r => setUserId(r.data.user?.id ?? null)) }, [])

  // ── Import wizard state ──
  const fileRef = useRef<HTMLInputElement>(null)
  const [parsed, setParsed] = useState<{ headers: string[]; rows: Record<string, string>[] } | null>(null)
  const [map, setMap] = useState({ email: NONE, first: NONE, last: NONE, company: NONE })
  const [batch, setBatch] = useState('')
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number } | null>(null)
  const [showManual, setShowManual] = useState(false)
  const [manual, setManual] = useState({ email: '', first: '', last: '', company: '', batch: '' })
  const [manualMsg, setManualMsg] = useState<string | null>(null)

  // ── List / selection state ──
  const [batchFilter, setBatchFilter] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [verifyProgress, setVerifyProgress] = useState<{ done: number; total: number } | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [selectedCampaigns, setSelectedCampaigns] = useState<Set<string>>(new Set())
  const [newCampaignName, setNewCampaignName] = useState('')
  const [selectedSegments, setSelectedSegments] = useState<Set<string>>(new Set())
  const [newSegmentName, setNewSegmentName] = useState('')
  const [addResult, setAddResult] = useState<{ added: number; skipped: number } | null>(null)

  // ── Queries ──
  const { data: leads, isLoading } = useQuery({
    queryKey: ['imported_leads', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('imported_leads')
        .select('*')
        .eq('user_id', userId!)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as ImportedLead[]
    },
  })

  const { data: campaigns, refetch: refetchCampaigns } = useQuery({
    queryKey: ['campaigns_list_import', userId],
    enabled: !!userId,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaigns').select('id, name, description, audience, kind')
        .eq('user_id', userId!).order('name')
      if (error) throw error
      return (data ?? []) as Campaign[]
    },
  })

  const { data: segmentsList, refetch: refetchSegments } = useQuery({
    queryKey: ['segments_list_import', userId],
    enabled: !!userId,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('segments').select('id, name').eq('user_id', userId!).order('name')
      if (error) throw error
      return (data ?? []) as { id: string; name: string }[]
    },
  })

  // Campaign chips per imported lead
  const { data: leadCampaigns } = useQuery({
    queryKey: ['imported_lead_campaigns', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from('lead_campaign_assignments')
        .select('imported_lead_id, campaigns ( id, name )')
        .eq('user_id', userId!)
        .eq('target', 'imported')
      const m = new Map<string, { id: string; name: string }[]>()
      for (const r of (data ?? []) as Record<string, unknown>[]) {
        const lid = r.imported_lead_id as string | null
        const camp = r.campaigns as { id: string; name: string } | null
        if (!lid || !camp) continue
        const arr = m.get(lid) ?? []
        arr.push(camp); m.set(lid, arr)
      }
      return m
    },
  })

  // ── Import flow ──
  const onFile = (file: File) => {
    Papa.parse<Record<string, string>>(file, {
      header: true, skipEmptyLines: true,
      complete: res => {
        const headers = res.meta.fields ?? []
        setParsed({ headers, rows: res.data })
        setMap({
          email:   guess(headers, 'email', 'mail'),
          first:   guess(headers, 'firstname', 'first', 'forename', 'name'),
          last:    guess(headers, 'lastname', 'last', 'surname'),
          company: guess(headers, 'company', 'organisation', 'organization', 'business'),
        })
        if (!batch) setBatch(file.name.replace(/\.[^.]+$/, ''))
      },
    })
  }

  const importMut = useMutation({
    mutationFn: async () => {
      if (!parsed || map.email === NONE) throw new Error('Map the email column first')
      const uid = userId ?? (await supabase.auth.getUser()).data.user?.id
      if (!uid) throw new Error('Not authenticated')

      const seen = new Set<string>()
      const rows = parsed.rows.flatMap(r => {
        const email = (r[map.email] ?? '').trim().toLowerCase()
        if (!email || !email.includes('@') || seen.has(email)) return []
        seen.add(email)
        return [{
          user_id: uid,
          email,
          first_name: map.first !== NONE ? (r[map.first] ?? '').trim() || null : null,
          last_name:  map.last !== NONE ? (r[map.last] ?? '').trim() || null : null,
          company:    map.company !== NONE ? (r[map.company] ?? '').trim() || null : null,
          batch: batch.trim() || null,
        }]
      })
      if (!rows.length) throw new Error('No valid email rows found')

      let imported = 0
      for (let i = 0; i < rows.length; i += 500) {
        const chunk = rows.slice(i, i + 500)
        const { error, count } = await supabase
          .from('imported_leads')
          .upsert(chunk, { onConflict: 'user_id,email', ignoreDuplicates: true, count: 'exact' })
        if (error) throw error
        imported += count ?? chunk.length
      }
      return { imported, skipped: parsed.rows.length - rows.length }
    },
    onSuccess: r => {
      setImportResult(r); setParsed(null); setBatch('')
      if (fileRef.current) fileRef.current.value = ''
      qc.invalidateQueries({ queryKey: ['imported_leads'] })
    },
  })

  // ── Verify domains (client-side MX, grouped per domain) ──
  const verifyDomains = async () => {
    const todo = (leads ?? []).filter(l => l.domain_verified === null)
    if (!todo.length) return
    const byDomain = new Map<string, string[]>()
    for (const l of todo) {
      const d = emailDomain(l.email)
      if (!d) continue
      const arr = byDomain.get(d) ?? []
      arr.push(l.id); byDomain.set(d, arr)
    }
    const domains = [...byDomain.entries()]
    setVerifyProgress({ done: 0, total: todo.length })
    let done = 0
    let idx = 0
    const worker = async () => {
      while (idx < domains.length) {
        const [domain, ids] = domains[idx++]
        const ok = await domainHasMx(domain)
        await supabase.from('imported_leads').update({ domain_verified: ok }).in('id', ids)
        done += ids.length
        setVerifyProgress({ done, total: todo.length })
      }
    }
    await Promise.all(Array.from({ length: Math.min(6, domains.length) }, () => worker()))
    setVerifyProgress(null)
    qc.invalidateQueries({ queryKey: ['imported_leads'] })
  }

  // ── Link to campaign ──
  // Add a single lead by hand. Verifies the domain straight away so the new row
  // lands with the same green/red status a CSV import would give it.
  const manualMut = useMutation({
    mutationFn: async () => {
      const uid = userId ?? (await supabase.auth.getUser()).data.user?.id
      if (!uid) throw new Error('Not authenticated')
      const email = manual.email.trim().toLowerCase()
      if (!email.includes('@')) throw new Error('Enter a valid email address')

      const domain = emailDomain(email)
      const verified = domain ? await domainHasMx(domain) : false

      const { error } = await supabase.from('imported_leads').insert({
        user_id: uid,
        email,
        first_name: manual.first.trim() || null,
        last_name: manual.last.trim() || null,
        company: manual.company.trim() || null,
        batch: manual.batch.trim() || null,
        domain_verified: verified,
      })
      if (error) {
        // unique (user_id, email)
        if (error.code === '23505') throw new Error('That email is already in your imported leads')
        throw error
      }
      return { email, verified }
    },
    onSuccess: ({ email, verified }) => {
      setManualMsg(`Added ${email} — ${verified ? 'domain verified ✓' : 'no mail server found ✗'}`)
      setManual(m => ({ email: '', first: '', last: '', company: '', batch: m.batch }))
      qc.invalidateQueries({ queryKey: ['imported_leads'] })
    },
  })

  const addMut = useMutation({
    mutationFn: async () => {
      const uid = userId ?? (await supabase.auth.getUser()).data.user?.id
      const res = await authedFetch('/api/leads/add-to-campaigns', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ importedLeadIds: Array.from(selected), campaignIds: Array.from(selectedCampaigns), segmentIds: Array.from(selectedSegments), userId: uid }),
      })
      if (!res.ok) throw new Error(await res.text())
      return res.json() as Promise<{ added: number; skipped: number }>
    },
    onSuccess: r => {
      setAddResult(r); setShowModal(false)
      setSelected(new Set()); setSelectedCampaigns(new Set()); setSelectedSegments(new Set())
      // Adding a lead to a campaign creates/links a central contact — refresh
      // every view that reads contacts so it shows up consistently at once.
      for (const k of [['imported_leads'], ['imported_lead_campaigns'], ['contacts'], ['campaign_members'], ['contact-memberships']]) {
        qc.invalidateQueries({ queryKey: k })
      }
    },
  })

  const createSegMut = useMutation({
    mutationFn: async () => {
      if (!newSegmentName.trim()) throw new Error('Segment name required')
      const uid = userId ?? (await supabase.auth.getUser()).data.user?.id
      if (!uid) throw new Error('Not authenticated')
      const { data, error } = await supabase
        .from('segments').insert({ name: newSegmentName.trim(), user_id: uid })
        .select('id').single()
      if (error) throw error
      return data
    },
    onSuccess: d => {
      setSelectedSegments(prev => new Set([...prev, d.id]))
      setNewSegmentName('')
      refetchSegments()
    },
  })

  const createMut = useMutation({
    mutationFn: async () => {
      if (!newCampaignName.trim()) throw new Error('Campaign name required')
      const uid = userId ?? (await supabase.auth.getUser()).data.user?.id
      if (!uid) throw new Error('Not authenticated')
      const { data, error } = await supabase
        .from('campaigns')
        .insert({ name: newCampaignName.trim(), user_id: uid, audience: 'director' })
        .select('id').single()
      if (error) throw error
      return data
    },
    onSuccess: d => {
      setSelectedCampaigns(prev => new Set([...prev, d.id]))
      setNewCampaignName('')
      refetchCampaigns()
    },
  })

  const delMut = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.from('imported_leads').delete().in('id', ids).eq('user_id', userId!)
      if (error) throw error
    },
    onSuccess: () => { setSelected(new Set()); qc.invalidateQueries({ queryKey: ['imported_leads'] }) },
  })

  // ── Derived ──
  const batches = useMemo(() => [...new Set((leads ?? []).map(l => l.batch).filter(Boolean))] as string[], [leads])
  const shown = useMemo(() =>
    (leads ?? []).filter(l => !batchFilter || l.batch === batchFilter), [leads, batchFilter])
  const uncheckedCount = useMemo(() => (leads ?? []).filter(l => l.domain_verified === null).length, [leads])

  const toggle = (id: string) =>
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  const selectAll = () =>
    setSelected(new Set(shown.filter(l => l.domain_verified === true).map(l => l.id)))

  // Imported leads are plain email addresses, so unlike CH officers they are not
  // restricted to director-audience campaigns. Show every campaign.
  const allCampaigns = campaigns ?? []

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Lead Engine (Import)</h1>
        <p className="text-xs text-gray-500 mt-0.5">Import leads from a spreadsheet (CSV) · verify their email domains · link to campaigns</p>
      </div>

      {/* Banners */}
      {importResult && (
        <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-2.5 text-xs text-green-700 flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
          Imported <strong className="mx-0.5">{importResult.imported}</strong> lead{importResult.imported !== 1 ? 's' : ''}.
          {importResult.skipped > 0 && <span className="opacity-70">({importResult.skipped} rows skipped — missing/duplicate email)</span>}
          <button onClick={() => setImportResult(null)} className="ml-auto"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
      {addResult && (
        <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-2.5 text-xs text-green-700 flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
          Added <strong className="mx-0.5">{addResult.added}</strong> to campaigns.
          {addResult.skipped > 0 && <span className="opacity-70">({addResult.skipped} skipped — domain not verified)</span>}
          <button onClick={() => setAddResult(null)} className="ml-auto"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
      {verifyProgress && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-xs text-emerald-700 flex items-center gap-2">
          <RefreshCw className="w-3.5 h-3.5 animate-spin shrink-0" />
          Verifying domains… {verifyProgress.done} of {verifyProgress.total}
        </div>
      )}

      {/* Add one manually */}
      <div className="card p-4">
        <button
          onClick={() => setShowManual(v => !v)}
          className="flex items-center gap-2 text-sm font-semibold text-gray-900"
        >
          <UserPlus className="w-4 h-4 text-gray-400" />
          Add a lead manually
          <span className="text-xs font-normal text-gray-400">— one at a time, email is all you need</span>
        </button>

        {showManual && (
          <div className="mt-3 space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              <input
                className="input py-1.5 text-xs lg:col-span-2"
                placeholder="Email *"
                type="email"
                value={manual.email}
                onChange={e => setManual({ ...manual, email: e.target.value })}
                onKeyDown={e => { if (e.key === 'Enter' && manual.email.trim()) manualMut.mutate() }}
              />
              <input className="input py-1.5 text-xs" placeholder="First name"
                value={manual.first} onChange={e => setManual({ ...manual, first: e.target.value })} />
              <input className="input py-1.5 text-xs" placeholder="Last name"
                value={manual.last} onChange={e => setManual({ ...manual, last: e.target.value })} />
              <input className="input py-1.5 text-xs" placeholder="Company"
                value={manual.company} onChange={e => setManual({ ...manual, company: e.target.value })} />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input className="input py-1.5 text-xs w-48" placeholder="Batch label (optional)"
                value={manual.batch} onChange={e => setManual({ ...manual, batch: e.target.value })} />
              <button
                onClick={() => manualMut.mutate()}
                disabled={!manual.email.trim() || manualMut.isPending}
                className="btn-primary text-xs py-1.5 disabled:opacity-40"
              >
                {manualMut.isPending ? 'Checking domain…' : 'Add lead'}
              </button>
              <span className="text-[11px] text-gray-400">
                The domain is checked for a mail server as soon as you add it.
              </span>
            </div>
            {manualMut.isError && (
              <p className="text-xs text-red-600">{describeError(manualMut.error)}</p>
            )}
            {manualMsg && !manualMut.isError && (
              <p className="text-xs text-green-700 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" />{manualMsg}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Import card */}
      <div className="card p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="btn-secondary text-xs flex items-center gap-2 cursor-pointer">
            <Upload className="w-3.5 h-3.5" />
            Choose CSV file
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
              onChange={e => e.target.files?.[0] && onFile(e.target.files[0])} />
          </label>
          <p className="text-[11px] text-gray-400">Spreadsheet must be saved as CSV. First row = column headings.</p>
        </div>

        {parsed && (
          <div className="border-t border-gray-100 pt-3 space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {([
                ['email', 'Email *'], ['first', 'First name'], ['last', 'Last name'], ['company', 'Company'],
              ] as const).map(([k, label]) => (
                <label key={k} className="block">
                  <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
                  <select value={map[k]} onChange={e => setMap(m => ({ ...m, [k]: e.target.value }))}
                    className="input w-full py-1.5 text-[11px] mt-1">
                    <option value={NONE}>— not mapped —</option>
                    {parsed.headers.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </label>
              ))}
              <label className="block">
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Batch label</span>
                <input type="text" value={batch} onChange={e => setBatch(e.target.value)}
                  placeholder="e.g. Expo list Mar 26" className="input w-full py-1.5 text-[11px] mt-1" />
              </label>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => importMut.mutate()} disabled={map.email === NONE || importMut.isPending}
                className="btn-primary text-xs flex items-center gap-2 disabled:opacity-40">
                <Upload className="w-3.5 h-3.5" />
                {importMut.isPending ? 'Importing…' : `Import ${parsed.rows.length.toLocaleString()} rows`}
              </button>
              <button onClick={() => { setParsed(null); if (fileRef.current) fileRef.current.value = '' }}
                className="text-[11px] text-gray-400 hover:text-gray-600">Cancel</button>
              {importMut.isError && <p className="text-[11px] text-red-500">{(importMut.error as Error).message}</p>}
            </div>
          </div>
        )}
      </div>

      {/* Leads table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="py-16 text-center text-xs text-gray-400">Loading…</div>
        ) : !(leads ?? []).length ? (
          <div className="py-16 text-center">
            <Users className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-gray-500 font-medium text-xs">No imported leads yet</p>
            <p className="text-gray-400 text-[11px] mt-1">Upload a CSV above to bring in your first list</p>
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex-wrap">
              <button onClick={selectAll}
                className="flex items-center gap-1.5 text-[11px] font-medium text-gray-600 hover:text-brand-600 px-2.5 py-1 rounded border border-gray-200 bg-white hover:border-brand-400 transition-colors">
                <CheckSquare className="w-3 h-3" /> Select All deliverable
              </button>
              {selected.size > 0 && (
                <>
                  <button onClick={() => setSelected(new Set())}
                    className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500 hover:text-gray-700 px-2.5 py-1 rounded border border-gray-200 bg-white transition-colors">
                    <Square className="w-3 h-3" /> Clear ({selected.size})
                  </button>
                  <button onClick={() => delMut.mutate(Array.from(selected))}
                    className="flex items-center gap-1.5 text-[11px] font-medium text-red-500 hover:text-red-700 px-2.5 py-1 rounded border border-red-200 bg-white transition-colors">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </>
              )}
              <div className="w-px h-4 bg-gray-200" />
              {uncheckedCount > 0 && (
                <button onClick={verifyDomains} disabled={verifyProgress !== null}
                  className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 hover:text-emerald-900 px-2.5 py-1 rounded border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors disabled:opacity-50">
                  <CheckCircle2 className="w-3 h-3" /> Verify domains ({uncheckedCount})
                </button>
              )}
              <button onClick={() => { refetchCampaigns(); setShowModal(true) }} disabled={selected.size === 0}
                className="flex items-center gap-1.5 text-[11px] font-medium text-white bg-brand-600 hover:bg-brand-700 px-3 py-1 rounded border border-brand-600 transition-colors disabled:opacity-40">
                <Link2 className="w-3 h-3" /> Link → Campaign ({selected.size})
              </button>
              {batches.length > 0 && (
                <select value={batchFilter} onChange={e => setBatchFilter(e.target.value)}
                  className="input py-1 text-[11px] ml-auto">
                  <option value="">All batches</option>
                  {batches.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              )}
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left">
                    <th className="px-3 py-2 w-8" />
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Name</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Email</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Company</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Batch</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Campaigns</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map(l => {
                    const deliverable = l.domain_verified === true
                    const camps = leadCampaigns?.get(l.id) ?? []
                    return (
                      <tr key={l.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                        <td className="pl-3 pr-1 py-2">
                          {deliverable && (
                            <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggle(l.id)}
                              className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs font-medium text-gray-800">
                          {[l.first_name, l.last_name].filter(Boolean).join(' ') || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-3 py-2">
                          {deliverable ? (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium bg-emerald-50 text-emerald-700 border-emerald-300">
                              <CheckCircle2 className="w-2.5 h-2.5" />{l.email}<span className="opacity-70">· deliverable</span>
                            </span>
                          ) : l.domain_verified === false ? (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium bg-red-50 text-red-600 border-red-300">
                              <AlertCircle className="w-2.5 h-2.5" />{l.email}<span className="opacity-70">· no mail server</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium bg-gray-50 text-gray-500 border-gray-300">
                              <AlertCircle className="w-2.5 h-2.5" />{l.email}<span className="opacity-70">· unchecked</span>
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-[11px] text-gray-600">{l.company ?? '—'}</td>
                        <td className="px-3 py-2 text-[10px] text-gray-400">{l.batch ?? '—'}</td>
                        <td className="px-3 py-2">
                          {camps.length > 0 ? (
                            <div className="flex flex-wrap gap-0.5">
                              {camps.map(c => (
                                <span key={c.id} className="text-[9px] px-1.5 py-0.5 bg-brand-50 text-brand-700 border border-brand-200 rounded-full whitespace-nowrap">{c.name}</span>
                              ))}
                            </div>
                          ) : <span className="text-[10px] text-gray-300">—</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-[11px] text-gray-500">
              {shown.length.toLocaleString()} lead{shown.length !== 1 ? 's' : ''}{batchFilter ? ` in “${batchFilter}”` : ''}
            </div>
          </>
        )}
      </div>

      {/* Link to campaign modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Link to campaign</h2>
                <p className="text-xs text-gray-500 mt-0.5">{selected.size} imported lead{selected.size !== 1 ? 's' : ''} selected</p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Campaigns</p>
                {!allCampaigns.length ? (
                  <p className="text-xs text-gray-400 italic">No campaigns yet — create one below.</p>
                ) : (
                  <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
                    {allCampaigns.map(c => (
                      <label key={c.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-200 hover:border-brand-300 cursor-pointer transition-colors">
                        <input type="checkbox" checked={selectedCampaigns.has(c.id)}
                          onChange={() => setSelectedCampaigns(prev => { const n = new Set(prev); n.has(c.id) ? n.delete(c.id) : n.add(c.id); return n })}
                          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="text-xs font-medium text-gray-800">{c.name}</p>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-medium ${c.audience === 'company' ? 'bg-indigo-50 text-indigo-600 border-indigo-200' : 'bg-brand-50 text-brand-600 border-brand-200'}`}>
                              {c.audience === 'company' ? '🏢 Company' : '👤 Directors'}
                            </span>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-medium ${c.kind === 'sequence' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                              {c.kind === 'sequence' ? '🔁 Sequence' : '📢 Broadcast'}
                            </span>
                          </div>
                          {c.description && <p className="text-[10px] text-gray-400 truncate">{c.description}</p>}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Or create new campaign</p>
                <div className="flex gap-2">
                  <input type="text" placeholder="Campaign name…" value={newCampaignName}
                    onChange={e => setNewCampaignName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && newCampaignName.trim() && createMut.mutate()}
                    className="input flex-1 text-xs py-2" />
                  <button onClick={() => createMut.mutate()} disabled={!newCampaignName.trim() || createMut.isPending}
                    className="btn-secondary text-xs py-2 flex items-center gap-1 disabled:opacity-40">
                    <Plus className="w-3 h-3" />{createMut.isPending ? '…' : 'Create'}
                  </button>
                </div>
              </div>

              {/* Segments */}
              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">And / or link to segment(s)</p>
                {(segmentsList ?? []).length > 0 && (
                  <div className="space-y-1 max-h-36 overflow-y-auto pr-1 mb-2">
                    {(segmentsList ?? []).map(s => (
                      <label key={s.id} className="flex items-center gap-3 px-3 py-1.5 rounded-lg border border-gray-200 hover:border-indigo-300 cursor-pointer transition-colors">
                        <input type="checkbox" checked={selectedSegments.has(s.id)}
                          onChange={() => setSelectedSegments(prev => { const n = new Set(prev); n.has(s.id) ? n.delete(s.id) : n.add(s.id); return n })}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                        <span className="text-xs text-gray-700">{s.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input type="text" placeholder="New segment name…" value={newSegmentName}
                    onChange={e => setNewSegmentName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && newSegmentName.trim() && createSegMut.mutate()}
                    className="input flex-1 text-xs py-2" />
                  <button onClick={() => createSegMut.mutate()} disabled={!newSegmentName.trim() || createSegMut.isPending}
                    className="btn-secondary text-xs py-2 flex items-center gap-1 disabled:opacity-40">
                    <Plus className="w-3 h-3" />{createSegMut.isPending ? '…' : 'Create'}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
              <button onClick={() => setShowModal(false)} className="btn-secondary text-xs">Cancel</button>
              <button onClick={() => addMut.mutate()} disabled={(selectedCampaigns.size === 0 && selectedSegments.size === 0) || addMut.isPending}
                className="btn-primary flex items-center gap-2 text-xs disabled:opacity-40">
                <Send className="w-3 h-3" />
                {addMut.isPending ? 'Adding…' : `Add to ${[
                  selectedCampaigns.size ? `${selectedCampaigns.size} campaign${selectedCampaigns.size !== 1 ? 's' : ''}` : '',
                  selectedSegments.size ? `${selectedSegments.size} segment${selectedSegments.size !== 1 ? 's' : ''}` : '',
                ].filter(Boolean).join(' + ') || '…'}`}
              </button>
            </div>
            {addMut.isError && (
              <p className="px-5 pb-4 text-[11px] text-red-500">{addMut.error instanceof Error ? addMut.error.message : 'Failed'}</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
